/**
 * indoor_body.ts — A BODY INSIDE THE ACTIVE INTERIOR IS LIT BY THE ROOM (the interiors programme, slice H, 2026-10-02).
 *
 * WHY. Interiors are UNLIT by law (interiors.md law 9: a room must read without scene light — PBR goes black at night).
 * The bodies standing in them were not: versona residents, a venue's keeper and customers, other players and your own
 * third-person body wore their lit PBR materials, lit by the outdoor sun and sky through the walls by day and by
 * nothing at night — black silhouettes in a readable room. The fix is at the BODY, not the room: while a body stands
 * inside the active interior it wears an UNLIT TWIN of each of its materials — its own colour map, times the ROOM's
 * light (a level and a warm tint from the look's strip light, `interior_style.bodyLightOf`), times a soft form term
 * from the surface's world normal (lit from above, never flat cardboard, never from the sun) — and the moment it
 * leaves it wears its own material again.
 *
 * NEVER A SHARED MATERIAL MUTATED. A twin is a NEW node material per SOURCE material (cached; every body of one rig
 * shares its twin, as they share the source). The room's light is TWO SHARED UNIFORMS (only one interior is active at
 * a time), so every twin program is built once and a room change is a uniform write — no rebuild, no cache-key churn.
 * A source with its own `positionNode` (the dual-quaternion skinning patch) hands it to the twin, as the skin material
 * does, so the body deforms identically; it writes `normalLocal`, so the form term follows the skinned normal.
 *
 * THREE PATHS USE IT:
 *   · `presentIndoors(root, on)` — a skinned versona actor, a player's rig body, a player's static scanned body: each
 *     mesh's material is swapped to its twin while `on`, the material it wore remembered; called every frame for a body
 *     it concerns (cheap: the mesh list is cached per root). If anything else re-dresses the mesh while indoors (the
 *     skin-shading registry flips a body to its skin material when a bake lands), the next frame records the new base
 *     and twins THAT — the registry's choice is never lost, and is what comes back outdoors.
 *   · `indoorTwin(material)` — the instanced crowd twins: EnvironmentModule draws an indoor versona from a third
 *     instancer per body built over the same geometry with this material (a material cannot change per instance).
 *   · `setIndoorBodyLight(light | null)` — the visit sets the room's light at the door and clears it on the way out.
 *
 * Lever: `localStorage atlas:interior-bodies = 'off'` → every body keeps its own lit material indoors (the black bodies
 * of 2026-10-01), read once a second.
 */
import * as THREE from 'three/webgpu';
import { Fn, uniform, vec3, vec4, float, materialColor, normalWorld, saturate, mix } from 'three/tsl';
import { atlas } from '../state.js';
import { enterables, interiorSpec, FLOOR_H } from './building_interiors.js';

export const INDOOR_BODY_LEVER_KEY = 'atlas:interior-bodies';
/** The form term's floor: a surface facing straight down keeps this share of the room's light (a body is lit from the
 *  ceiling's strip and the room's walls — never from one side, never to black). */
export const INDOOR_FORM_FLOOR = 0.62; // decided with pictures: 0.72 read flat

let leverAt = -1e9, leverOn = true;
export function indoorBodiesEnabled(read?: (k: string) => string | null): boolean {
  if (read) { try { return read(INDOOR_BODY_LEVER_KEY) !== 'off'; } catch { return true; } }
  const now = typeof performance !== 'undefined' ? performance.now() : 0;
  if (now - leverAt < 1000) return leverOn;
  leverAt = now;
  try { leverOn = typeof localStorage === 'undefined' || localStorage.getItem(INDOOR_BODY_LEVER_KEY) !== 'off'; } catch { leverOn = true; }
  return leverOn;
}

/** The room's light on a body: a linear-space tint (r, g, b) and a level. */
export interface IndoorBodyLight { tint: [number, number, number]; level: number }
const NEUTRAL: IndoorBodyLight = { tint: [1, 1, 1], level: 1 };

// ONE pair of uniforms for every twin: only one interior is active at a time.
const uTint = uniform(new THREE.Color(1, 1, 1));
const uLevel = uniform(1);
let current: IndoorBodyLight = NEUTRAL;

/** The visit sets the room's light at the door (`interior_style.bodyLightOf`), null on the way out. */
export function setIndoorBodyLight(light: IndoorBodyLight | null): void {
  current = light ?? NEUTRAL;
  (uTint.value as THREE.Color).setRGB(current.tint[0], current.tint[1], current.tint[2]);
  uLevel.value = current.level;
}
export function indoorBodyLight(): IndoorBodyLight { return current; }

// ── the twins ─────────────────────────────────────────────────────────────────────────────────────────────────────
type Src = THREE.Material & {
  map?: THREE.Texture | null; color?: THREE.Color; alphaMap?: THREE.Texture | null; positionNode?: unknown;
  isNodeMaterial?: boolean;
};
const twins = new WeakMap<THREE.Material, THREE.MeshBasicNodeMaterial>();
const isTwin = new WeakSet<THREE.Material>();
export const indoorBodyStats = { twins: 0, swapsIn: 0, swapsOut: 0, roots: 0, rebases: 0 };

/** The unlit twin of one source material (cached per source; the source is never touched). */
export function indoorTwin(src: THREE.Material): THREE.MeshBasicNodeMaterial {
  if (isTwin.has(src)) return src as THREE.MeshBasicNodeMaterial;
  const hit = twins.get(src);
  if (hit) return hit;
  const s = src as Src;
  const m = new THREE.MeshBasicNodeMaterial();
  const mm = m as unknown as THREE.MeshBasicMaterial;
  mm.map = s.map ?? null;
  if (s.color) mm.color.copy(s.color); else mm.color.set(0xffffff);
  mm.side = s.side; mm.transparent = s.transparent === true; mm.opacity = s.opacity ?? 1; mm.alphaTest = s.alphaTest ?? 0;
  mm.alphaMap = s.alphaMap ?? null;
  m.lights = false;
  m.fog = false;
  m.name = `${src.name || src.type}#indoor`;
  // The DQ patch (and anything else that deforms the body) rides along: the twin bends exactly as the source does.
  if (s.isNodeMaterial && s.positionNode) (m as unknown as { positionNode: unknown }).positionNode = s.positionNode;
  const hasMap = !!s.map;
  // THE BUILD-TIME LAW: the branch is decided here, before `Fn(` — a map makes `materialColor` a vec4 (its alpha is
  // the map's), no map leaves it a vec3.
  const form = saturate(normalWorld.y.mul(0.5).add(0.5));
  const light = vec3(uTint).mul(uLevel).mul(mix(float(INDOOR_FORM_FLOOR), float(1), form));
  m.colorNode = hasMap
    ? Fn(() => { const c = vec4(materialColor); return vec4(c.rgb.mul(light), c.a); })()
    : Fn(() => vec4(vec3(materialColor).mul(light), 1))();
  twins.set(src, m);
  isTwin.add(m);
  indoorBodyStats.twins++;
  return m;
}
export function isIndoorTwin(m: THREE.Material | null | undefined): boolean { return !!m && isTwin.has(m); }

// ── a body's meshes ──────────────────────────────────────────────────────────────────────────────────────────────
interface MeshRec { mesh: THREE.Mesh; base: THREE.Material | null }
interface RootRec { meshes: MeshRec[]; childCount: number; indoor: boolean }
const roots = new WeakMap<THREE.Object3D, RootRec>();

function meshesOf(root: THREE.Object3D, rec: RootRec | undefined): RootRec {
  // a body's subtree changes rarely (a worn model landing, a hand prop): re-walk when its direct children change
  if (rec && rec.childCount === root.children.length) return rec;
  const prev = new Map<THREE.Mesh, MeshRec>();
  if (rec) for (const r of rec.meshes) prev.set(r.mesh, r);
  const meshes: MeshRec[] = [];
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || Array.isArray(m.material) || !m.material) return;
    if (o.userData.indoorBodySkip) return; // a carried prop, a plate, a ring — not the body
    meshes.push(prev.get(m) ?? { mesh: m, base: null });
  });
  const out: RootRec = { meshes, childCount: root.children.length, indoor: rec?.indoor ?? false };
  roots.set(root, out);
  if (!rec) indoorBodyStats.roots++;
  return out;
}

/**
 * Dress a body for where it stands: `on` = inside the active interior (each mesh wears its material's indoor twin),
 * else its own materials. Idempotent and cheap per frame; with the lever off every call restores and does nothing more.
 */
export function presentIndoors(root: THREE.Object3D, on: boolean): void {
  let rec = roots.get(root);
  const want = on && indoorBodiesEnabled();
  if (!want && (!rec || !rec.indoor)) return; // the common case outdoors: nothing recorded, nothing to do
  rec = meshesOf(root, rec);
  for (const r of rec.meshes) {
    const cur = r.mesh.material as THREE.Material;
    if (want) {
      if (isTwin.has(cur)) continue;
      // anything that re-dressed the mesh while it stood indoors (the skin registry) is the new base
      if (r.base && cur !== r.base) indoorBodyStats.rebases++;
      r.base = cur;
      r.mesh.material = indoorTwin(cur);
      indoorBodyStats.swapsIn++;
    } else if (isTwin.has(cur) && r.base) {
      r.mesh.material = r.base;
      indoorBodyStats.swapsOut++;
    }
  }
  rec.indoor = want;
}

/** Is a point inside a building box (absolute metres; `yaw` as three's rotation.y)? The active interior's walls ± a
 *  margin, floor to roof. The bodies module asks it for players and versonas not flagged by their own rows. */
export function insideBox(b: { x: number; z: number; yaw: number; groundY: number; hw: number; hd: number; floors: number; floorH: number }, x: number, y: number, z: number, margin = 0.2): boolean {
  const c = Math.cos(b.yaw), s = Math.sin(b.yaw);
  const dx = x - b.x, dz = z - b.z;
  const lx = c * dx - s * dz, lz = s * dx + c * dz;
  return Math.abs(lx) <= b.hw + margin && Math.abs(lz) <= b.hd + margin && y >= b.groundY - 1 && y <= b.groundY + b.floors * b.floorH + 0.5;
}

/** The interior the player stands in, as a box (memoised per interior id; null outdoors or mid-rebuild). */
let roomMemo: { id: string; box: Parameters<typeof insideBox>[0] } | null = null;
export function activeRoomBox(): Parameters<typeof insideBox>[0] | null {
  const id = atlas.interiorId;
  if (!id) return null;
  if (!roomMemo || roomMemo.id !== id) {
    const b = enterables.get(id);
    if (!b) return null;
    const sp = interiorSpec(b.w, b.h, b.d, b.key);
    roomMemo = { id, box: { x: b.x, z: b.z, yaw: b.yaw, groundY: b.groundY, hw: sp.hw, hd: sp.hd, floors: sp.floors, floorH: FLOOR_H } };
  }
  return roomMemo.box;
}
/** Does an absolute point stand inside the walls of the interior the player stands in? (false outdoors, fast) */
export function insideActiveInterior(x: number, y: number, z: number): boolean {
  const box = activeRoomBox();
  return !!box && insideBox(box, x, y, z);
}
